// Cookie utility functions for managing authentication tokens

export interface CookieOptions {
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

/**
 * Set a cookie with the given name, value, and options
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  if (typeof window === 'undefined') return;

  const {
    expires,
    path = '/',
    domain,
    secure = true, // Default to secure for production
    sameSite = 'lax',
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (expires) {
    const expiresDate = expires instanceof Date ? expires : new Date(Date.now() + expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${expiresDate.toUTCString()}`;
  }

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += `; secure`;
  }

  if (sameSite) {
    cookieString += `; samesite=${sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  if (typeof window === 'undefined') return null;

  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }

  return null;
};

/**
 * Delete a cookie by name
 */
export const deleteCookie = (name: string, path: string = '/', domain?: string): void => {
  if (typeof window === 'undefined') return;

  let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
  
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  document.cookie = cookieString;
};

/**
 * Check if cookies are enabled in the browser
 */
export const areCookiesEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;

  const testName = 'test_cookie';
  const testValue = 'test';
  
  setCookie(testName, testValue, { expires: 1 });
  const enabled = getCookie(testName) === testValue;
  deleteCookie(testName);
  
  return enabled;
};

// Auth-specific cookie utilities
export const AUTH_TOKEN_COOKIE = 'authToken';
export const AUTH_USER_COOKIE = 'authUser';
export const ADMIN_TOKEN_COOKIE = 'adminToken';
export const ADMIN_USER_COOKIE = 'adminUser';

/**
 * Set authentication token in cookie
 */
export const setAuthToken = (token: string): void => {
  setCookie(AUTH_TOKEN_COOKIE, token, {
    expires: 7, // 7 days
    secure: import.meta.env.PROD || false,
    sameSite: 'lax',
    path: '/',
  });
};

/**
 * Get authentication token from cookie
 */
export const getAuthToken = (): string | null => {
  return getCookie(AUTH_TOKEN_COOKIE);
};

/**
 * Remove authentication token cookie
 */
export const removeAuthToken = (): void => {
  deleteCookie(AUTH_TOKEN_COOKIE);
};

/**
 * Set authentication user data in cookie
 */
export const setAuthUser = (user: any): void => {
  setCookie(AUTH_USER_COOKIE, JSON.stringify(user), {
    expires: 7, // 7 days
    secure: import.meta.env.PROD || false,
    sameSite: 'lax',
    path: '/',
  });
};

/**
 * Get authentication user data from cookie
 */
export const getAuthUser = (): any | null => {
  const userString = getCookie(AUTH_USER_COOKIE);
  if (!userString) return null;
  
  try {
    return JSON.parse(userString);
  } catch (error) {
    console.error('Error parsing auth user data:', error);
    return null;
  }
};

/**
 * Remove authentication user data cookie
 */
export const removeAuthUser = (): void => {
  deleteCookie(AUTH_USER_COOKIE);
};

/**
 * Set admin token in cookie
 */
export const setAdminToken = (token: string): void => {
  setCookie(ADMIN_TOKEN_COOKIE, token, {
    expires: 1, // 1 day for admin sessions
    secure: import.meta.env.PROD || false,
    sameSite: 'lax',
  });
};

/**
 * Get admin token from cookie
 */
export const getAdminToken = (): string | null => {
  return getCookie(ADMIN_TOKEN_COOKIE);
};

/**
 * Remove admin token cookie
 */
export const removeAdminToken = (): void => {
  deleteCookie(ADMIN_TOKEN_COOKIE);
};

/**
 * Set admin user data in cookie
 */
export const setAdminUser = (user: any): void => {
  setCookie(ADMIN_USER_COOKIE, JSON.stringify(user), {
    expires: 1, // 1 day for admin sessions
    secure: import.meta.env.PROD || false,
    sameSite: 'lax',
  });
};

/**
 * Get admin user data from cookie
 */
export const getAdminUser = (): any | null => {
  const userString = getCookie(ADMIN_USER_COOKIE);
  if (!userString) return null;
  
  try {
    return JSON.parse(userString);
  } catch (error) {
    console.error('Error parsing admin user cookie:', error);
    return null;
  }
};

/**
 * Remove admin user cookie
 */
export const removeAdminUser = (): void => {
  deleteCookie(ADMIN_USER_COOKIE);
};

/**
 * Migrate authentication data from localStorage to cookies
 * This helps users who already have tokens stored in localStorage.
 * Note: For new deployments, prefer server-set HttpOnly auth cookies (see CODE_REVIEW_REPORT.md).
 */
export const migrateAuthFromLocalStorage = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    const localStorageToken = localStorage.getItem('token');
    const localStorageUser = localStorage.getItem('user');
    
    if (localStorageToken && !getAuthToken()) {
      // Migrate token to cookie
      setAuthToken(localStorageToken);
      console.log('Migrated auth token from localStorage to cookies');
    }
    
    if (localStorageUser && !getAuthUser()) {
      // Migrate user data to cookie
      try {
        const userData = JSON.parse(localStorageUser);
        setAuthUser(userData);
        console.log('Migrated user data from localStorage to cookies');
      } catch (error) {
        console.error('Error parsing localStorage user data during migration:', error);
      }
    }
    
    // Clean up localStorage after successful migration
    if (localStorageToken && getAuthToken()) {
      localStorage.removeItem('token');
    }
    if (localStorageUser && getAuthUser()) {
      localStorage.removeItem('user');
    }
  } catch (error) {
    console.error('Error during localStorage to cookie migration:', error);
  }
};

/**
 * Clear all authentication data (cookies and localStorage)
 */
export const clearAllAuthCookies = (): void => {
  removeAuthToken();
  removeAuthUser();
  removeAdminToken();
  removeAdminUser();
  
  // Also clear any remaining localStorage tokens
  if (typeof window !== 'undefined') {
    localStorage.removeItem('token');
  }
};
